import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { ColorType } from '@adornis/chemistry/elements/theming/coloring-system.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { useTheme, useTranslation } from './util.js';

export type Action<Tparams extends any[] = []> = {
  icon: string;
  text?: string;
  color?: 'primary' | 'danger' | ColorType;
  waitForAction?: boolean;
  confirm?: {
    heading: string;
    text: string;
    highlightDecline: boolean;
    acceptText: string;
    declineText: string;
  };
  callback: (...args: Tparams) => void;
};

const handleError = (translate, err) => {
  console.error(err);
  const errMessage = (() => {
    if (err?.extensions?.translationKey) {
      return translate('errors', err.extensions.translationKey);
    }

    const errCode = err?.message ?? 'unknown';
    return translate('err', errCode);
  })();

  return XSnackbar.show(errMessage);
};

export const renderAction = stateful<{ action: Action }>(({ action }) => {
  const theme = useTheme();
  const tr = useTranslation();

  const _action = async () => {
    let doIT = true;
    if (action.confirm) {
      const cd = action.confirm;
      doIT = await XNativeDialog.confirm(
        html`
          <x-flex space="md">
            <x-h3 ${css({ alignSelf: 'center' })}> ${cd.heading} </x-h3>
            <x-text> ${cd.text} </x-text>
          </x-flex>
        `,
        {
          highlightDecline: cd.highlightDecline,
          acceptText: cd.acceptText,
          declineText: cd.declineText,
        },
      );
    }

    if (doIT)
      try {
        const maybePromise = action.callback() as any;
        if (maybePromise instanceof Promise && (action.waitForAction ?? true)) {
          await XNativeDialog.waitFor(maybePromise);
        }
      } catch (e) {
        if (!tr) {
          console.error(e);
          return;
        }
        handleError(tr.translate.bind(tr), e);
      }
  };

  const primary = theme?.colors.accent.shade(400);
  const color = (() => {
    if (!action.color) return primary;
    if (action.color === 'primary') return primary;
    if (action.color === 'danger') return theme?.colors.tone.error;
    return action.color;
  })();

  // prettier-ignore
  return html`<tr-button-small 
      icon=${action.icon}
      .action=${() => _action()}
      .color=${color}>
      ${action.text
        ? html` <tr-text bold> ${action.text} </tr-text> `
        : nothing}
    </tr-button-small>`;
});
